/**
 * PhaseNav - Left sidebar navigation for workshop phases.
 * Desktop/iPad (≥701px): fixed 220px sidebar with flow controls at foot.
 * Phone (≤700px): list trigger + overlay slide-in drawer; flow controls in sticky bottom bar.
 */
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Check, List } from 'lucide-react';
import { media } from '../../styles/breakpoints';
import WorkshopFlowBar from './WorkshopFlowBar';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const DesktopNav = styled.nav`
  width: 220px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.3);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;

  ${media.sm} {
    display: none;
  }
`;

/* Only the step list scrolls when the chain outgrows the sidebar; the flow
   controls at the foot stay visible (fix 2026-10-19). */
const StepScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const MobileToggle = styled.button`
  display: none;

  ${media.sm} {
    display: flex;
    position: fixed;
    top: calc(var(--kuutio-admin-header-height, 60px) + 52px);
    left: 12px;
    z-index: 60;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(16, 16, 24, 0.95);
    color: #fff;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  }
`;

const MobileOverlay = styled.div`
  display: none;

  ${media.sm} {
    display: ${p => (p.$open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    z-index: 70;
    background: rgba(0, 0, 0, 0.5);
  }
`;

const MobilePanel = styled.nav`
  display: none;

  ${media.sm} {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    width: min(300px, 85vw);
    height: 100%;
    height: var(--kuutio-vh, 100vh);
    padding-top: var(--kuutio-admin-header-height, 60px);
    background: #0d0d14;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.4);
    overflow-y: auto;
    transform: translateX(${p => (p.$open ? '0' : '-100%')});
    transition: transform 0.25s ease;
    z-index: 80;
  }
`;

const DayLabel = styled.div`
  padding: 16px 16px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.06em;

  &:not(:first-of-type) {
    margin-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    padding-top: 16px;
  }
`;

const PhaseItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: ${p =>
    p.$viewing ? 'rgba(74, 158, 255, 0.12)' :
    p.$flowActive ? 'rgba(34, 197, 94, 0.08)' :
    'transparent'};
  border: none;
  border-left: 3px solid ${p =>
    p.$viewing ? '#4a9eff' :
    p.$flowActive ? '#22c55e' :
    'transparent'};
  color: ${p =>
    p.$viewing ? '#fff' :
    p.$flowActive ? '#fff' :
    p.$done ? 'rgba(255,255,255,0.5)' :
    'rgba(255,255,255,0.6)'};
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100%;
  min-height: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const StepNumber = styled.span`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
  background: ${p =>
    p.$active ? '#4a9eff' :
    p.$done ? 'rgba(34, 197, 94, 0.2)' :
    'rgba(255,255,255,0.08)'};
  color: ${p =>
    p.$active ? '#fff' :
    p.$done ? '#22c55e' :
    '#888'};
`;

const StepName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const LiveChip = styled.span`
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #22c55e;
`;

function PhaseList({
  steps,
  currentStepId,
  activePhaseIndex,
  onPhaseClick,
  onSetupClick,
}) {
  const { t } = useTranslation();
  // The chain has no days: one list, numbered by position (#683)
  const navItems = steps.map((step, idx) => ({ ...step, _index: idx }));

  const currentStepIndex = steps.findIndex((s) => s.id === currentStepId);

  return (
    <>
      {onSetupClick && (
        <>
          <DayLabel>{t('workshopOrchestrator.setup')}</DayLabel>
          <PhaseItem type="button" onClick={onSetupClick} title={t('workshopOrchestrator.setup')}>
            <StepNumber>0</StepNumber>
            <StepName>{t('workshopOrchestrator.setup')}</StepName>
          </PhaseItem>
        </>
      )}
      {navItems.map((step) => {
        const isFlowActive = step.id === currentStepId;
        const isViewing = step._index === activePhaseIndex;
        const isDone = step._index < currentStepIndex;
        return (
          <React.Fragment key={step.id}>
            <PhaseItem
              type="button"
              $viewing={isViewing}
              $flowActive={isFlowActive && !isViewing}
              $done={isDone}
              onClick={() => onPhaseClick(step._index, step.id)}
              title={step.name}
            >
              <StepNumber $active={isViewing || isFlowActive} $done={isDone}>
                {isDone
                  ? <Check size={12} />
                  : step._index + 1}
              </StepNumber>
              <StepName>{step.name}</StepName>
              {isFlowActive && <LiveChip>● {t('workshopOrchestrator.live')}</LiveChip>}
            </PhaseItem>
          </React.Fragment>
        );
      })}
    </>
  );
}

export default function PhaseNav({
  steps = [],
  currentStepId,
  activePhaseIndex,
  flowState,
  timer = null,
  onPhaseClick,
  onFlowAction,
  onTimerAdjust = null,
  onSetupClick = null,
  onStart = null,
  showMobileFlowBar = true,
  gate = null,
}) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const handlePhaseClick = (index, stepId) => {
    setDrawerOpen(false);
    onPhaseClick(index, stepId);
  };

  const handleSetupClick = onSetupClick
    ? () => {
        setDrawerOpen(false);
        onSetupClick();
      }
    : null;

  const listProps = {
    steps,
    currentStepId,
    activePhaseIndex,
    onPhaseClick: handlePhaseClick,
    onSetupClick: handleSetupClick,
  };

  const flowProps = {
    steps,
    currentStepId,
    flowState,
    timer,
    onFlowAction,
    onTimerAdjust,
    onStart,
    gate,
  };

  return (
    <>
      <DesktopNav>
        <StepScroll>
          <PhaseList {...listProps} />
        </StepScroll>
        <WorkshopFlowBar {...flowProps} variant="desktop" />
      </DesktopNav>

      <MobileToggle
        type="button"
        aria-label={t('workshopOrchestrator.navAria')}
        aria-expanded={drawerOpen}
        onClick={() => setDrawerOpen((open) => !open)}
      >
        <List size={20} />
      </MobileToggle>

      <MobileOverlay $open={drawerOpen} onClick={() => setDrawerOpen(false)} />
      <MobilePanel $open={drawerOpen} aria-hidden={!drawerOpen}>
        <PhaseList {...listProps} />
      </MobilePanel>

      {showMobileFlowBar && (
        <WorkshopFlowBar {...flowProps} variant="mobile" />
      )}
    </>
  );
}
